import React from 'react';
import './Skeleton.css';

// ============================================================
// Skeleton Loader Components
// ============================================================

interface SkeletonLineProps {
  width?: string | number;
  height?: string | number;
  size?: 'sm' | 'md' | 'lg' | 'title';
  style?: React.CSSProperties;
}

/**
 * Single skeleton line — for text placeholders
 */
export const SkeletonLine: React.FC<SkeletonLineProps> = ({
  width,
  height,
  size = 'lg',
  style,
}) => {
  return (
    <div
      className={`skeleton skeleton-line skeleton-line--${size}`}
      style={{
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
        ...style,
      }}
    />
  );
};

interface SkeletonCardProps {
  lines?: number;
  showAvatar?: boolean;
  style?: React.CSSProperties;
}

/**
 * Card skeleton — for card-based layouts
 */
export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  lines = 3,
  showAvatar = false,
  style,
}) => {
  return (
    <div className="skeleton-card" style={style}>
      {showAvatar && (
        <div className="skeleton-row">
          <SkeletonAvatar size="md" />
          <div className="skeleton-row__content">
            <SkeletonLine size="md" />
            <SkeletonLine size="sm" />
          </div>
        </div>
      )}
      <SkeletonLine size="title" />
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonLine
          key={i}
          size={i === lines - 1 ? 'md' : 'lg'}
        />
      ))}
    </div>
  );
};

interface SkeletonAvatarProps {
  size?: 'sm' | 'md' | 'lg';
  style?: React.CSSProperties;
}

/**
 * Avatar skeleton — circle placeholder
 */
export const SkeletonAvatar: React.FC<SkeletonAvatarProps> = ({
  size = 'md',
  style,
}) => {
  return (
    <div
      className={`skeleton skeleton-avatar skeleton-avatar--${size}`}
      style={style}
    />
  );
};

interface SkeletonTableProps {
  rows?: number;
  columns?: number;
  style?: React.CSSProperties;
}

/**
 * Table skeleton — for data tables
 */
export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  rows = 5,
  columns = 4,
  style,
}) => {
  const columnWidths = Array.from({ length: columns }).map(
    (_, i) => `${Math.max(15, 100 / columns - (i % 2 ? 5 : 0))}%`
  );

  return (
    <div className="skeleton-table" style={style}>
      {/* Header */}
      <div className="skeleton-table__header">
        {columnWidths.map((w, i) => (
          <div
            key={`h-${i}`}
            className="skeleton skeleton-table__cell"
            style={{ width: w, flex: 'none' }}
          />
        ))}
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={`r-${rowIndex}`} className="skeleton-table__row">
          {columnWidths.map((w, colIndex) => (
            <div
              key={`c-${rowIndex}-${colIndex}`}
              className="skeleton skeleton-table__cell"
              style={{ width: w, flex: 'none' }}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

interface SkeletonPageProps {
  type?: 'cards' | 'table' | 'list';
}

/**
 * Full page skeleton — replaces PageLoader
 */
export const SkeletonPage: React.FC<SkeletonPageProps> = ({ type = 'cards' }) => {
  return (
    <div className="skeleton-container">
      {/* Page title */}
      <SkeletonLine size="title" width="30%" height="28px" />

      {/* Subtitle */}
      <SkeletonLine size="md" width="50%" />

      {type === 'cards' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginTop: '16px' }}>
          <SkeletonCard lines={3} showAvatar />
          <SkeletonCard lines={3} showAvatar />
          <SkeletonCard lines={3} showAvatar />
          <SkeletonCard lines={3} showAvatar />
        </div>
      )}

      {type === 'table' && (
        <SkeletonTable rows={8} columns={5} style={{ marginTop: '16px' }} />
      )}

      {type === 'list' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton-row" style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
              <SkeletonAvatar size="sm" />
              <div className="skeleton-row__content">
                <SkeletonLine size="md" />
                <SkeletonLine size="sm" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default {
  SkeletonLine,
  SkeletonCard,
  SkeletonAvatar,
  SkeletonTable,
  SkeletonPage,
};
